'use client';
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '#pplx/frontend/aether/components/Button/index.js';
import { ButtonPrimitive } from '#pplx/frontend/aether/primitives/ButtonPrimitive/index.js';
import { TextPrimitive } from '#pplx/frontend/aether/primitives/TextPrimitive/index.js';
import { FileUploadDropZone } from '#pplx/frontend/apps/ask/ask-input/file-upload-ask-input/FileUploadDropZone.js';
import { FileUploadPreview } from '#pplx/frontend/apps/ask/ask-input/file-upload-ask-input/FileUploadPreview.js';
import { useDropZone } from '#pplx/frontend/components/hooks/useDropZone.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import classNames from 'classnames';
import React, { useCallback, useMemo, useRef } from 'react';
import { defineMessage, FormattedMessage, useIntl } from 'react-intl';
export const FILE_UPLOAD_LIMIT_REACHED_MESSAGE = defineMessage({
    defaultMessage: "Some files weren't uploaded because the file limit was reached.",
    id: 'ec1731e924',
    description: 'Warning toast shown when a file selection exceeds the file limit',
});
function resolvePreviewStatus(file) {
    if (file.status === 'failed')
        return 'failed';
    if (file.status === 'success' && !file.isThumbnailPending)
        return 'success';
    return 'uploading';
}
export const FileUploadAskInput = React.memo(function FileUploadAskInput({ files, onFilesSelected, onRemoveFile, acceptedFileTypes, dropZoneDescription, maxFiles = 1, dropZoneClassName, appearance = 'drop-zone', dropBehavior = 'file-element-only', disabled, onOverLimitRejected, }) {
    const { $t } = useIntl();
    const fileInputRef = useRef(null);
    const atLimit = files.length >= maxFiles;
    const remainingSlots = maxFiles - files.length;
    const handleSelection = useCallback((selected) => {
        const accepted = selected.slice(0, Math.max(0, remainingSlots));
        const rejected = selected.slice(accepted.length);
        if (accepted.length > 0)
            onFilesSelected(accepted);
        if (rejected.length > 0)
            onOverLimitRejected?.(rejected, selected.length);
    }, [onFilesSelected, onOverLimitRejected, remainingSlots]);
    const handleDrop = useCallback((items) => {
        const dropped = [];
        for (let i = 0; i < items.length; i++) {
            const file = items[i]?.getAsFile();
            if (file)
                dropped.push(file);
        }
        if (dropped.length === 0)
            return;
        handleSelection(dropped);
    }, [handleSelection]);
    const validateDragItems = useCallback((items) => Array.from(items).filter((it) => it.kind === 'file').length <= remainingSlots, [remainingSlots]);
    const { isFileOver, hasInvalidFiles: isInvalidOver, rootProps, } = useDropZone({
        onDrop: handleDrop,
        // Keep viewport ownership at the limit so rejected drops report the limit
        // instead of falling through to the global composer. Selection still rejects them.
        disabled: disabled || (atLimit && dropBehavior === 'file-element-only'),
        dropBehavior,
        validateDragItems,
    });
    const handleClickUpload = useCallback(() => {
        if (disabled || atLimit)
            return;
        fileInputRef.current?.click();
    }, [atLimit, disabled]);
    const handleFileInputChange = useCallback((e) => {
        const selected = e.target.files;
        if (selected && selected.length > 0) {
            handleSelection(Array.from(selected));
        }
        e.target.value = '';
    }, [handleSelection]);
    const containerClassName = useMemo(() => classNames('flex w-full flex-col border-dashed rounded-xl border transition-colors duration-150 cursor-pointer hover:bg-subtle', {
        'border-negative-strong': isFileOver && isInvalidOver,
        'border-super-strong': isFileOver && !isInvalidOver,
        'border-base': !isFileOver,
    }), [isFileOver, isInvalidOver]);
    return (_jsxs("div", { className: "flex flex-col gap-2", children: [files.map((file) => (_jsx(FileUploadPreview, { fileName: file.fileName, fileSize: file.fileSize, status: resolvePreviewStatus(file), thumbnailSource: file.thumbnailUrl, error: file.error, appearance: appearance, onRemove: () => onRemoveFile(file.id) }, file.id))), !atLimit && (_jsxs("div", { ...rootProps, className: classNames({
                    'flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 rounded-lg py-0.5 transition-colors duration-150': appearance === 'compact',
                    'bg-super-soft px-2': appearance === 'compact' && isFileOver && !isInvalidOver,
                    'bg-negative-soft px-2': appearance === 'compact' && isFileOver && isInvalidOver,
                }), children: [appearance === 'compact' ? (_jsxs(_Fragment, { children: [_jsx(Button, { variant: "secondary", size: "small", leadingAccessory: $icon('paperclip'), onClick: handleClickUpload, disabled: disabled, track: false, children: _jsx(FormattedMessage, { defaultMessage: "Attach files", description: "Compact action for attaching files to a file upload field", id: "a30f7f89ab" }) }), isInvalidOver ? (_jsx(TextPrimitive, { variant: "tiny", color: "negative", className: "min-w-0", children: _jsx(FormattedMessage, { defaultMessage: "Up to {maxFiles, plural, one {# file} other {# files}}", description: "Compact file upload limit shown while too many files are dragged over the picker", id: "389dc39e02", values: { maxFiles } }) })) : isFileOver ? (_jsx(TextPrimitive, { variant: "tiny", color: "light", className: "min-w-0", children: _jsx(FormattedMessage, { defaultMessage: "Drop files here", description: "Compact file upload action shown while files are dragged over the picker", id: "328e6e5179" }) })) : dropZoneDescription ? (_jsx(TextPrimitive, { variant: "tiny", color: "light", className: "min-w-0", children: dropZoneDescription })) : null] })) : (_jsx(ButtonPrimitive, { className: containerClassName, onClick: handleClickUpload, disabled: disabled, "aria-label": $t({
                            defaultMessage: '{maxFiles, plural, one {Upload a file} other {Upload files}}',
                            id: 'a3dc5449f3',
                            description: 'Aria label for the file upload drop zone button',
                        }, { maxFiles }), children: _jsx(FileUploadDropZone, { description: dropZoneDescription, className: dropZoneClassName, maxFiles: maxFiles, isOver: isFileOver, isInvalidOver: isInvalidOver }) })), _jsx("input", { ref: fileInputRef, type: "file", accept: acceptedFileTypes, multiple: maxFiles > 1, className: "hidden", disabled: disabled || atLimit, onChange: handleFileInputChange })] }))] }));
});
//# sourceMappingURL=FileUploadAskInput.js.map